import { Box, Typography } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import type { SpecimenAuditSeverity } from "../../../services/api";
import { colors } from "../../../theme/colors";
import {
  normaliseStatus,
  severityChipStyle,
  statusColor,
  statusDisplayLabel,
} from "../utils";

const pillSx = {
  borderRadius: "24px",
  padding: "3px 12px",
  display: "inline-flex",
  alignItems: "center",
  fontSize: "12px",
  fontWeight: 500,
  lineHeight: "18px",
  whiteSpace: "nowrap" as const,
};

// Coloured status pill (uses the shared STATUS_COLOR_MAP).
export const StatusPill = ({ status }: { status?: string | null }) => {
  if (!status) return null;
  const label = statusDisplayLabel(status);
  return (
    <Box
      component="span"
      title={label}
      sx={{
        ...pillSx,
        // Labels run long ("IN OT DROP OFF PENDING"). Ellipsise instead of
        // wrapping so a pill can never spill out of a fixed-height table row;
        // the title attribute keeps the full label reachable on hover.
        display: "inline-block",
        maxWidth: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
        // Pastel fill + dark text, as on the specimen listing.
        backgroundColor: statusColor(status),
        color: colors.lighterBlack,
      }}
    >
      {label}
    </Box>
  );
};

// A transition between two statuses. Many activity rows record an action
// (CREATED / PRINTED) without moving the specimen, so `from` and `to` are the
// same status — rendering "X → X" reads as a change that never happened. Those
// collapse to a single pill with no connector; so does a one-sided transition.
export const StatusTransition = ({
  from,
  to,
  layout = "row",
}: {
  from?: string | null;
  to?: string | null;
  /** "column" stacks the pills with a down arrow — the feed's narrow column. */
  layout?: "row" | "column";
}) => {
  const isSameStatus =
    Boolean(from) && Boolean(to) && normaliseStatus(from) === normaliseStatus(to);
  const showConnector = Boolean(from) && Boolean(to) && !isSameStatus;
  const isColumn = layout === "column";

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isColumn ? "column" : "row",
        alignItems: "center",
        justifyContent: "center",
        gap: isColumn ? 0 : 0.75,
        minWidth: 0,
        width: isColumn ? "100%" : undefined,
        overflow: "hidden",
      }}
    >
      {/* Collapsed and one-sided transitions both render as the single status
          that is actually known. */}
      <StatusPill status={showConnector ? from : (to ?? from)} />
      {showConnector &&
        (isColumn ? (
          <ArrowDownwardIcon
            aria-hidden
            sx={{ my: "1px", fontSize: 14, color: colors.grey, flexShrink: 0 }}
          />
        ) : (
          <Box
            component="span"
            sx={{ color: colors.grey, fontSize: 16, flexShrink: 0 }}
          >
            →
          </Box>
        ))}
      {showConnector && <StatusPill status={to} />}
    </Box>
  );
};

// Neutral outline pill for meta tags such as "Recalculated".
export const TagPill = ({
  label,
  bg,
  color,
}: {
  label: string;
  bg: string;
  color: string;
}) => (
  <Box component="span" sx={{ ...pillSx, backgroundColor: bg, color }}>
    {label}
  </Box>
);

// Severity pill, shared between the feed and the inspector.
export const SeverityPill = ({
  severity,
}: {
  severity?: SpecimenAuditSeverity;
}) => {
  const style = severityChipStyle(severity);
  return (
    <Box component="span" sx={{ ...pillSx, backgroundColor: style.bg, color: style.color }}>
      <Typography component="span" sx={{ fontSize: "12px", fontWeight: 500 }}>
        {style.label}
      </Typography>
    </Box>
  );
};

export default StatusPill;
